import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ExcelJS from "exceljs";
import api from "../services/api"; // Centralized authenticated Axios instance

const EcomCommissions = () => {
  const [salesmen, setSalesmen] = useState([]);
  const [activeSalesmanId, setActiveSalesmanId] = useState(null);
  const [commissionReport, setCommissionReport] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // View Mode Switch state ('channels' or 'groups')
  const [viewMode, setViewMode] = useState("channels");

  // Dynamic tracking for manual adjustments and bonuses globally mapped by manager period signature
  const [adjustmentsLog, setAdjustmentsLog] = useState({});
  const [bonusLog, setBonusLog] = useState({});
  const [year, setYear] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.getFullYear();
  });
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return String(d.getMonth() + 1);
  });

  const monthsList = [
    { value: "1", name: "January" },
    { value: "2", name: "February" },
    { value: "3", name: "March" },
    { value: "4", name: "April" },
    { value: "5", name: "May" },
    { value: "6", name: "June" },
    { value: "7", name: "July" },
    { value: "8", name: "August" },
    { value: "9", name: "September" },
    { value: "10", name: "October" },
    { value: "11", name: "November" },
    { value: "12", name: "December" },
  ];

  // Unique key to hold separate manual entries for different periods/managers
  const currentAllocationKey = `${activeSalesmanId}-${year}-${month}`;
  const currentAdjustment = adjustmentsLog[currentAllocationKey] ?? "";
  const currentBonus = bonusLog[currentAllocationKey] ?? "";

  // Dynamic Excel Export Engine (Formatted for Accounting and Print Layouts)
  // Dynamic Excel Export Engine (Formatted for Accounting and Print Layouts)
  // Dynamic Excel Export Engine (Formatted for Accounting and Print Layouts)
  const handleExportToExcel = async () => {
    if (!commissionReport || commissionReport.length === 0) {
      alert("No active reporting dataset available to export.");
      return;
    }
    const report = commissionReport[0];
    const monthName =
      monthsList.find((m) => String(m.value) === String(month))?.name || month;

    // ---- Style palette (accounting workbook look) ----
    const COLOR = {
      headerFill: "FF1E293B", // slate-800
      headerText: "FFFFFFFF",
      titleText: "FF1E293B",
      subtitleText: "FF64748B", // slate-500
      zebraFill: "FFF8FAFC", // slate-50
      borderGray: "FFCBD5E1", // slate-300
      summaryLabel: "FF334155", // slate-700
      grandFill: "FFEEF2FF", // indigo-50
      grandText: "FF3730A3", // indigo-800
    };

    const thinBorder = {
      top: { style: "thin", color: { argb: COLOR.borderGray } },
      bottom: { style: "thin", color: { argb: COLOR.borderGray } },
      left: { style: "thin", color: { argb: COLOR.borderGray } },
      right: { style: "thin", color: { argb: COLOR.borderGray } },
    };

    // Standard Excel accounting format
    const accountingFmt =
      '_-$* #,##0.00_-;[Red]-$* #,##0.00_-;_-$* "-"??_-;_-@_-';
    const percentageFmt = '0.00"%";[Red]-0.00"%"';
    const flatDollarFmt = "_-$* #,##0.00_-;[Red]-$* #,##0.00_-";

    const COLS = 7; // A..G

    // ---- Repeating title block (prints on every page via printTitlesRow) ----
    const titleText = `${report.name} — ${
      viewMode === "channels" ? "Channel" : "Group"
    } Commission Report`;
    const subtitleText = `${monthName} ${year}  •  Generated ${new Date().toLocaleDateString(
      "en-US",
    )}`;

    const exportRows = [
      [titleText],
      [subtitleText],
      [],
      [
        viewMode === "channels" ? "CHANNEL ID" : "GROUP ID",
        viewMode === "channels" ? "CHANNEL NAME" : "GROUP NAME",
        "CURRENT SALES",
        "PREVIOUS SALES",
        "UP/DOWN (%)",
        "COMM RATE",
        "PAYOUT AMOUNT",
      ],
    ];

    const headerRowIdx = exportRows.length - 1; // 0-indexed row of the actual column header row

    let totalCommissionCombined = 0;
    let fallbackRate = 0;
    const channelStartRow = exportRows.length;

    // Export dataset switches context dynamically depending on current active view
    const dataRecords =
      viewMode === "channels" ? report.channels : report.groups;

    if (dataRecords && dataRecords.length > 0) {
      dataRecords.forEach((item) => {
        totalCommissionCombined += item.commission?.amount || 0;
        const numericPct = parseFloat(item.sales?.upDownPercent) || 0;
        const numericRate = parseFloat(item.commission?.rate) || 0;
        if (!item.commission?.isFlatAmount && numericRate > 0) {
          fallbackRate = numericRate;
        }
        exportRows.push([
          viewMode === "channels"
            ? item.channelId
            : item.groupId?.toUpperCase(),
          viewMode === "channels"
            ? item.channelName || `Channel ${item.channelId}`
            : `${item.groupId?.toUpperCase()} MARKETPLACES`,
          item.sales?.currentSales,
          item.sales?.previousSales,
          numericPct,
          item.commission?.isFlatAmount ? item.commission?.rate : numericRate,
          item.commission?.amount,
        ]);
      });
    } else {
      exportRows.push(["No configuration matrix entities found."]);
    }

    const parsedAdjustment = parseFloat(currentAdjustment) || 0;
    const parsedBonus = parseFloat(currentBonus) || 0;

    // fallbackRate is stored as a raw percentage number (e.g. 0.2 meaning 0.2%),
    // same as every COMM RATE cell above — so it must be divided by 100 before
    // being applied to a dollar amount, exactly like standardAppliedRate does
    // in the on-screen calculation below.
    const commissionFromAdjustment = parsedAdjustment * (fallbackRate / 100);

    // Append the custom Adjustment row into the main itemized listings array block
    if (parsedAdjustment !== 0) {
      exportRows.push([
        "—",
        "ADJUSTMENT",
        parsedAdjustment,
        0,
        0,
        fallbackRate,
        commissionFromAdjustment,
      ]);
    }

    const channelEndRow = exportRows.length - 1;
    const totalAdjustedCommission =
      totalCommissionCombined + commissionFromAdjustment;
    const grandTotalCalculated = totalAdjustedCommission + parsedBonus;

    // ---- Column totals row (Current Sales / Previous Sales / Up-Down % / Payout) ----
    const totalCurrentSales =
      dataRecords?.reduce((acc, c) => acc + (c.sales?.currentSales || 0), 0) ||
      0;
    const totalPreviousSales =
      dataRecords?.reduce((acc, c) => acc + (c.sales?.previousSales || 0), 0) ||
      0;
    const adjustedCurrentSalesTotal = totalCurrentSales + parsedAdjustment;
    const totalsUpDownPercent =
      totalPreviousSales !== 0
        ? parseFloat(
            (
              ((adjustedCurrentSalesTotal - totalPreviousSales) /
                totalPreviousSales) *
              100
            ).toFixed(2),
          )
        : 0;

    const totalsRow = exportRows.length;
    exportRows.push([
      "",
      "TOTAL",
      adjustedCurrentSalesTotal,
      totalPreviousSales,
      totalsUpDownPercent,
      "",
      totalAdjustedCommission,
    ]);

    exportRows.push(
      [],
      ["", "", "", "", "", "Sub Total:", totalAdjustedCommission],
      ["", "", "", "", "", "Bonus:", parsedBonus],
      ["", "", "", "", "", "Grand Total:", grandTotalCalculated],
    );

    const summaryStartRow = totalsRow + 2;
    const grandTotalRow = summaryStartRow + 2;
    const lastRow = grandTotalRow; // 0-indexed last populated row

    // ---- Build the ExcelJS workbook (page setup here is actually honored on write) ----
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Reconciliation Ledger", {
      pageSetup: {
        paperSize: 1, // Letter (use 9 for A4)
        orientation: "landscape",
        fitToPage: true,
        fitToWidth: 1, // all 7 columns on one page width
        fitToHeight: 0, // let rows flow across as many pages tall as needed
        margins: {
          left: 0.3,
          right: 0.3,
          top: 0.5,
          bottom: 0.5,
          header: 0.2,
          footer: 0.2,
        },
      },
      views: [{ showGridLines: false }],
    });

    worksheet.columns = [
      { width: 11 },
      { width: 24 },
      { width: 14 },
      { width: 14 },
      { width: 11 },
      { width: 11 },
      { width: 15 },
    ];

    worksheet.addRows(exportRows);

    // Repeat the title block + column header row on every printed page,
    // and constrain the print range to exactly what we built.
    worksheet.pageSetup.printTitlesRow = `1:${headerRowIdx + 1}`;
    worksheet.pageSetup.printArea = `A1:G${lastRow + 1}`;
    worksheet.headerFooter = {
      oddHeader: `&L&8&IElectronic Express — Confidential&R&8&I${monthName} ${year}`,
      oddFooter: `&C&8Page &P of &N&R&8Printed ${new Date().toLocaleDateString(
        "en-US",
      )}`,
    };

    // ---- Title block styling + merge across all columns ----
    worksheet.mergeCells(1, 1, 1, COLS);
    worksheet.getRow(1).height = 22;
    worksheet.getCell(1, 1).font = {
      bold: true,
      size: 14,
      color: { argb: COLOR.titleText },
    };
    worksheet.getCell(1, 1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    worksheet.mergeCells(2, 1, 2, COLS);
    worksheet.getRow(2).height = 16;
    worksheet.getCell(2, 1).font = {
      italic: true,
      size: 9,
      color: { argb: COLOR.subtitleText },
    };
    worksheet.getCell(2, 1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    // Header row styling (1-indexed excel row)
    const headerExcelRow = headerRowIdx + 1;
    for (let c = 1; c <= COLS; c++) {
      const cell = worksheet.getCell(headerExcelRow, c);
      cell.font = { bold: true, color: { argb: COLOR.headerText }, size: 10 };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLOR.headerFill },
      };
      cell.alignment = {
        horizontal: c === 1 || c === 2 ? "left" : "right",
        vertical: "middle",
      };
      cell.border = thinBorder;
    }

    // Data rows styling
    if (dataRecords && exportRows.length > headerRowIdx + 1) {
      for (let r = channelStartRow; r <= channelEndRow; r++) {
        const excelRow = r + 1;
        const isZebra = (r - channelStartRow) % 2 === 1;
        const rowLabel = exportRows[r][1];
        const isAdjustmentRow = rowLabel === "ADJUSTMENT";

        for (let c = 1; c <= COLS; c++) {
          const cell = worksheet.getCell(excelRow, c);
          cell.border = thinBorder;
          if (isZebra) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: COLOR.zebraFill },
            };
          }
          const baseFont = { size: 10, italic: isAdjustmentRow };
          cell.alignment = { vertical: "middle" };

          if (c === 1) {
            cell.font = { ...baseFont, bold: true };
            cell.alignment = { ...cell.alignment, horizontal: "center" };
          } else if (c === 2) {
            cell.font = {
              ...baseFont,
              bold: viewMode === "groups" || isAdjustmentRow,
            };
            cell.alignment = { ...cell.alignment, horizontal: "left" };
          } else if (c === 3 || c === 4) {
            cell.numFmt = accountingFmt;
            cell.font = baseFont;
            cell.alignment = { ...cell.alignment, horizontal: "right" };
          } else if (c === 5) {
            cell.numFmt = percentageFmt;
            cell.font = baseFont;
            cell.alignment = { ...cell.alignment, horizontal: "right" };
          } else if (c === 6) {
            const rowItem = dataRecords[r - channelStartRow];
            const isRowFlat = rowItem?.commission?.isFlatAmount;
            const rawValue = exportRows[r][c - 1];

            if (
              typeof rawValue === "number" &&
              !isRowFlat &&
              rawValue < 0.01 &&
              rawValue > 0
            ) {
              cell.value = `${(rawValue * 100).toFixed(2)}%`;
            } else {
              cell.numFmt = isRowFlat ? flatDollarFmt : percentageFmt;
            }
            cell.font = baseFont;
            cell.alignment = { ...cell.alignment, horizontal: "right" };
          } else if (c === 7) {
            cell.numFmt = accountingFmt;
            cell.font = { ...baseFont, bold: true };
            cell.alignment = { ...cell.alignment, horizontal: "right" };
          }
        }
      }
    }

    // ---- TOTAL row styling (sums Current Sales / Previous Sales / Up-Down % / Payout) ----
    {
      const totalsExcelRow = totalsRow + 1;
      const totalsBorder = {
        top: { style: "medium", color: { argb: COLOR.borderGray } },
        bottom: { style: "medium", color: { argb: COLOR.borderGray } },
        left: { style: "thin", color: { argb: COLOR.borderGray } },
        right: { style: "thin", color: { argb: COLOR.borderGray } },
      };
      for (let c = 1; c <= COLS; c++) {
        const cell = worksheet.getCell(totalsExcelRow, c);
        cell.border = totalsBorder;
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF1F5F9" },
        };
        cell.font = { bold: true, size: 10 };
        cell.alignment = { vertical: "middle", horizontal: "center" };

        if (c === 2) {
          cell.alignment = { ...cell.alignment, horizontal: "left" };
        } else if (c === 3 || c === 4) {
          cell.numFmt = accountingFmt;
          cell.alignment = { ...cell.alignment, horizontal: "right" };
        } else if (c === 5) {
          cell.numFmt = percentageFmt;
          cell.alignment = { ...cell.alignment, horizontal: "right" };
        } else if (c === 7) {
          cell.numFmt = accountingFmt;
          cell.font = {
            bold: true,
            size: 11,
            color: { argb: COLOR.grandText },
          };
          cell.alignment = { ...cell.alignment, horizontal: "right" };
        }
      }
    }

    const summaryLabels = ["Sub Total:", "Bonus:", "Grand Total:"];
    summaryLabels.forEach((_, idx) => {
      const r = summaryStartRow + idx;
      const excelRow = r + 1;
      const isGrand = r === grandTotalRow;

      const labelCell = worksheet.getCell(excelRow, 6);
      const valueCell = worksheet.getCell(excelRow, 7);

      const sharedFont = {
        bold: true,
        size: isGrand ? 11 : 10,
        color: { argb: isGrand ? COLOR.grandText : COLOR.summaryLabel },
      };
      const sharedFill = isGrand
        ? {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: COLOR.grandFill },
          }
        : undefined;
      const sharedBorder = isGrand
        ? {
            ...thinBorder,
            top: { style: "double", color: { argb: COLOR.grandText } },
          }
        : { bottom: { style: "hair", color: { argb: COLOR.borderGray } } };

      labelCell.font = sharedFont;
      labelCell.alignment = { horizontal: "right", vertical: "middle" };
      if (sharedFill) labelCell.fill = sharedFill;
      labelCell.border = sharedBorder;

      valueCell.numFmt = accountingFmt;
      valueCell.font = { ...sharedFont, size: isGrand ? 12 : 10 };
      valueCell.alignment = { horizontal: "right", vertical: "middle" };
      if (sharedFill) valueCell.fill = sharedFill;
      valueCell.border = sharedBorder;
    });

    const filename = `${report.name?.replace(/\s+/g, "_")}_${viewMode.toUpperCase()}_Commission_${monthName}_${year}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  // Unified accounting alignment engine ($ on left, number tightly wrapped in () on right)
  const renderAccountingCell = (value, customColorClass = "text-slate-800") => {
    const isNegative = value < 0;
    const absoluteValue = Math.abs(value);
    const formattedNumber = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absoluteValue);

    const activeColorClass = isNegative
      ? "text-red-600 font-bold"
      : customColorClass;

    return (
      <div
        className={`flex justify-between w-full font-mono text-sm tracking-tight ${activeColorClass}`}
      >
        <span>$</span>
        <span>
          {isNegative ? "(" : ""}
          {formattedNumber}
          {isNegative ? ")" : ""}
        </span>
      </div>
    );
  };

  useEffect(() => {
    const loadSalesmenProfiles = async () => {
      try {
        // Updated to use centralized api instance
        const res = await api.get("/ecomm/managers");
        const payload = res.data;
        if (payload.success && payload.data.length > 0) {
          setSalesmen(payload.data);
          setActiveSalesmanId(payload.data[0].salesmanId);
        }
      } catch (err) {
        console.error(
          "Failed to connect to team roster dataset config arrays",
          err,
        );
      }
    };
    loadSalesmenProfiles();
  }, []);

  useEffect(() => {
    if (!activeSalesmanId || !year) return;
    const fetchCommissionReportData = async () => {
      setIsLoading(true);
      try {
        // Updated to use centralized api instance
        const res = await api.get("/ecomm/commissions", {
          params: { year, month, salesmanId: activeSalesmanId },
        });
        const payload = res.data;
        if (payload.success) {
          const reportData = Array.isArray(payload.data)
            ? payload.data
            : [payload.data];
          setCommissionReport(reportData);
        }
      } catch (err) {
        console.error(
          "Error drawing live commission calculation pipelines",
          err,
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchCommissionReportData();
  }, [year, month, activeSalesmanId]);

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        {/* Page Header */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b pb-5 mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              E-Commerce Commission
            </h1>
          </div>
          <Link
            to="settings"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-800 font-medium text-sm py-2 px-4 rounded-lg shadow-sm transition-all"
          >
            <svg
              className="w-4 h-4 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Manager Settings
          </Link>
        </header>

        {/* Control Bar */}
        <div className="border-b border-slate-200 flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-1 -mb-[2px]">
            {salesmen.map((manager) => {
              const isSelected = manager.salesmanId === activeSalesmanId;

              const formatTabName = (fullName) => {
                if (!fullName) return "";
                const cleanName = fullName.replace("-", " ").trim();
                const parts = cleanName.split(/\s+/);
                const firstName = parts[0] || "";
                const numberMatch = fullName.match(/\b\d{4}\b/);
                const pbsIdNum = numberMatch ? numberMatch[0] : "";
                return pbsIdNum ? `${firstName} - ${pbsIdNum}` : firstName;
              };

              const formattedName = formatTabName(manager.name);

              return (
                <button
                  key={manager.salesmanId}
                  onClick={() => setActiveSalesmanId(manager.salesmanId)}
                  className={`px-4 py-2.5 text-sm font-semibold rounded-t-xl tracking-wide transition-all border-b-2 ${
                    isSelected
                      ? "border-indigo-600 bg-white text-indigo-600 font-bold shadow-sm"
                      : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                  }`}
                >
                  {formattedName}
                  <span className="ml-2 text-xs font-mono bg-slate-200/80 text-slate-600 px-1.5 py-0.5 rounded font-bold">
                    {manager.assignedChannelIds?.length || 0} Ch
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3 pb-2 md:pb-1">
            <div className="bg-slate-200/70 p-0.5 rounded-lg inline-flex items-center shadow-inner border">
              <button
                onClick={() => setViewMode("channels")}
                className={`text-xs px-2.5 py-1 rounded-md font-semibold transition-all duration-150 ${viewMode === "channels" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              >
                Channels View
              </button>
              <button
                onClick={() => setViewMode("groups")}
                className={`text-xs px-2.5 py-1 rounded-md font-semibold transition-all duration-150 ${viewMode === "groups" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              >
                Group View
              </button>
            </div>

            <div className="w-24">
              <input
                type="number"
                placeholder="Year"
                className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-indigo-600 font-mono text-center bg-white shadow-sm"
                value={year}
                onChange={(e) =>
                  setYear(parseInt(e.target.value) || new Date().getFullYear())
                }
              />
            </div>
            <div className="w-32">
              <select
                className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-indigo-600 bg-white shadow-sm text-center font-medium text-slate-700"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {monthsList.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleExportToExcel}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs py-1.5 px-3.5 rounded-lg shadow-sm transition-all tracking-wider uppercase"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Export Report
            </button>
          </div>
        </div>

        {/* Content Workspace */}
        {isLoading ? (
          <div className="bg-white border rounded-xl p-12 text-center text-sm text-slate-400 animate-pulse">
            Loading database routing parameters...
          </div>
        ) : (
          <div className="space-y-6">
            {commissionReport.map((report) => {
              if (!report) return null;

              const activeRecords =
                viewMode === "channels" ? report.channels : report.groups;

              const totalCommissionCombined =
                activeRecords?.reduce(
                  (acc, c) => acc + c.commission.amount,
                  0,
                ) || 0;

              let standardAppliedRate = 0;
              if (activeRecords && activeRecords.length > 0) {
                const foundItem = activeRecords.find(
                  (item) =>
                    !item.commission?.isFlatAmount &&
                    parseFloat(item.commission?.rate) > 0,
                );
                if (foundItem) {
                  standardAppliedRate =
                    parseFloat(foundItem.commission.rate) / 100;
                }
              }

              const totalCurrentSales =
                activeRecords?.reduce(
                  (acc, c) => acc + (c.sales?.currentSales || 0),
                  0,
                ) || 0;

              const totalPreviousSales =
                activeRecords?.reduce(
                  (acc, c) => acc + (c.sales?.previousSales || 0),
                  0,
                ) || 0;

              const parsedAdjustment = parseFloat(currentAdjustment) || 0;
              const parsedBonus = parseFloat(currentBonus) || 0;

              const adjustedCurrentSalesTotal =
                totalCurrentSales + parsedAdjustment;
              const commissionFromAdjustment =
                parsedAdjustment * standardAppliedRate;
              const finalSubtotalCommission =
                totalCommissionCombined + commissionFromAdjustment;

              const subtotalUpDownPercent =
                totalPreviousSales !== 0
                  ? parseFloat(
                      (
                        ((adjustedCurrentSalesTotal - totalPreviousSales) /
                          totalPreviousSales) *
                        100
                      ).toFixed(2),
                    )
                  : 0;

              const grandTotalCalculated =
                finalSubtotalCommission + parsedBonus;

              return (
                <div
                  key={report.salesmanId}
                  className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6"
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-mono text-xs uppercase">
                          <th className="p-3 font-bold min-w-[320px] whitespace-nowrap lg:whitespace-normal">
                            {viewMode === "channels"
                              ? "Channel ID / Name"
                              : "Group ID / Platform Name"}
                          </th>
                          <th className="p-3 text-right font-bold w-1/5">
                            Current Sales
                          </th>
                          <th className="p-3 text-right font-bold w-1/5">
                            Previous Sales
                          </th>
                          <th className="p-3 text-right font-bold w-1/5">
                            UP/DOWN
                          </th>
                          <th className="p-3 text-right font-bold w-1/5">
                            Comm Rate / Payout
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-mono">
                        {activeRecords?.map((item) => (
                          <tr
                            key={
                              viewMode === "channels"
                                ? item.channelId
                                : item.groupId
                            }
                            className="hover:bg-slate-50/50"
                          >
                            <td className="p-3 font-sans font-medium text-slate-700 flex items-center min-w-[320px]">
                              {viewMode === "channels" ? (
                                <>
                                  <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-mono mr-2 font-bold inline-block w-14 text-center">
                                    {item.channelId}
                                  </span>
                                  <span className="text-sm font-medium">
                                    {item.channelName ||
                                      `Channel ${item.channelId}`}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-1 rounded text-xs font-mono mr-4 font-extrabold uppercase tracking-wide inline-block w-28 text-center shrink-0">
                                    {item.groupId}
                                  </span>
                                  <span className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                                    {item.groupId} Marketplaces Total
                                  </span>
                                </>
                              )}
                            </td>
                            <td className="p-3 max-w-[180px]">
                              {renderAccountingCell(
                                item.sales.currentSales,
                                "font-semibold text-slate-800",
                              )}
                            </td>
                            <td className="p-3 max-w-[180px]">
                              {renderAccountingCell(
                                item.sales.previousSales,
                                "text-slate-500",
                              )}
                            </td>
                            <td className="p-3 max-w-[220px]">
                              <div className="flex items-center justify-end gap-2">
                                <span
                                  className={`text-xs font-bold shrink-0 ${item.sales.difference >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                                >
                                  {item.sales.difference < 0
                                    ? `(${Math.abs(item.sales.upDownPercent)}%)`
                                    : `${item.sales.upDownPercent}%`}
                                </span>
                              </div>
                            </td>
                            <td className="p-3 max-w-[200px]">
                              <div className="flex items-center justify-end gap-3">
                                <span className="text-xs text-slate-400 font-sans tracking-normal shrink-0">
                                  {item.commission.isFlatAmount ? "$" : ""}
                                  {item.commission.rate}
                                  {item.commission.isFlatAmount ? "" : "%"}
                                </span>
                                <div className="w-full">
                                  {renderAccountingCell(
                                    item.commission.amount,
                                    "font-bold text-indigo-600",
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {/* --- INLINE ADJUSTMENT ROW --- */}
                        {activeRecords && activeRecords.length > 0 && (
                          <tr className="bg-slate-50/40 border-t border-slate-200">
                            <td className="p-3 font-sans text-xs uppercase font-bold tracking-wider text-slate-900 text-left">
                              Adjustment
                            </td>
                            <td className="p-2 max-w-[180px]">
                              <div className="relative flex items-center w-full bg-white border border-slate-200 rounded-md shadow-sm px-2">
                                <span className="text-slate-400 text-sm font-semibold mr-1">
                                  $
                                </span>
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  className="w-full text-right font-mono text-sm tracking-tight text-slate-800 focus:outline-none bg-transparent py-1"
                                  value={currentAdjustment}
                                  onChange={(e) =>
                                    setAdjustmentsLog({
                                      ...adjustmentsLog,
                                      [currentAllocationKey]: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </td>
                            <td className="p-3 text-right text-slate-400">—</td>
                            <td className="p-3 text-right text-slate-400">—</td>
                            <td className="p-3 max-w-[200px]">
                              <div className="flex items-center justify-end gap-3">
                                <span className="text-xs text-slate-400 font-sans tracking-normal shrink-0 font-normal">
                                  {(standardAppliedRate * 100).toFixed(1)}%
                                </span>
                                <div className="w-full">
                                  {renderAccountingCell(
                                    commissionFromAdjustment,
                                    "font-bold text-indigo-500 italic",
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* --- SUBTOTAL ROW --- */}
                        {activeRecords && activeRecords.length > 0 && (
                          <tr className="bg-slate-50/80 font-bold border-t-2 border-slate-300 border-b-2">
                            <td className="p-3 font-sans text-xs uppercase tracking-wider text-slate-700 font-extrabold">
                              Subtotal
                            </td>
                            <td className="p-3 max-w-[180px]">
                              {renderAccountingCell(
                                adjustedCurrentSalesTotal,
                                "font-bold text-slate-800",
                              )}
                            </td>
                            <td className="p-3 max-w-[180px]">
                              {renderAccountingCell(
                                totalPreviousSales,
                                "font-bold text-slate-600",
                              )}
                            </td>
                            <td className="p-3 max-w-[220px]">
                              <div className="flex items-center justify-end gap-2">
                                <span
                                  className={`text-xs font-extrabold shrink-0 ${subtotalUpDownPercent >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                                >
                                  {subtotalUpDownPercent < 0
                                    ? `(${Math.abs(subtotalUpDownPercent)}%)`
                                    : `${subtotalUpDownPercent}%`}
                                </span>
                              </div>
                            </td>
                            <td className="p-3 max-w-[200px]">
                              <div className="flex items-center justify-end gap-3">
                                <span className="text-xs text-slate-400 font-sans tracking-normal shrink-0 font-normal">
                                  —
                                </span>
                                <div className="w-full">
                                  {renderAccountingCell(
                                    finalSubtotalCommission,
                                    "font-extrabold text-indigo-600",
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {(!activeRecords || activeRecords.length === 0) && (
                          <tr>
                            <td
                              colSpan="5"
                              className="p-6 text-center text-slate-400 italic font-sans"
                            >
                              No active parameters matched this configuration
                              matrix display profile view.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Manual Reconciliation Summary Ledger breakdown */}
                  <div className="flex justify-end pt-4">
                    <div className="w-full max-w-md border border-slate-200 rounded-lg overflow-hidden shadow-sm bg-slate-50/40">
                      <table className="w-full text-left border-collapse text-sm font-mono">
                        <tbody>
                          <tr className="border-b border-slate-200">
                            <td className="p-3 font-sans text-xs font-bold text-slate-400 uppercase tracking-wider">
                              Sub Total Payout
                            </td>
                            <td className="p-3 w-44 bg-white/50">
                              {renderAccountingCell(
                                finalSubtotalCommission,
                                "font-bold text-slate-700",
                              )}
                            </td>
                          </tr>
                          <tr className="border-b border-slate-200">
                            <td className="p-3 font-sans text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">
                              Bonus (Flat)
                            </td>
                            <td className="p-2 w-44 bg-white">
                              <div className="relative flex items-center">
                                <span className="absolute left-2.5 text-slate-400 text-sm">
                                  $
                                </span>
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  className="w-full text-right border border-slate-200 rounded-md pl-6 pr-2 py-1 font-mono text-sm tracking-tight text-slate-800 focus:outline-indigo-600 focus:border-indigo-600"
                                  value={currentBonus}
                                  onChange={(e) =>
                                    setBonusLog({
                                      ...bonusLog,
                                      [currentAllocationKey]: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                          <tr className="bg-slate-100/60 font-bold">
                            <td className="p-3 font-sans text-xs font-extrabold text-slate-900 uppercase tracking-wider">
                              Grand Total
                            </td>
                            <td className="p-3 w-44 bg-indigo-50/30 border-b-4 border-double border-indigo-600">
                              {renderAccountingCell(
                                grandTotalCalculated,
                                "text-base font-extrabold text-indigo-600",
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EcomCommissions;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import * as XLSX from "xlsx-js-style";
// import api from "../services/api"; // Centralized authenticated Axios instance

// const EcomCommissions = () => {
//   const [salesmen, setSalesmen] = useState([]);
//   const [activeSalesmanId, setActiveSalesmanId] = useState(null);
//   const [commissionReport, setCommissionReport] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);

//   // View Mode Switch state ('channels' or 'groups')
//   const [viewMode, setViewMode] = useState("channels");

//   // Dynamic tracking for manual adjustments and bonuses globally mapped by manager period signature
//   const [adjustmentsLog, setAdjustmentsLog] = useState({});
//   const [bonusLog, setBonusLog] = useState({});
//   const [year, setYear] = useState(() => {
//     const d = new Date();
//     d.setMonth(d.getMonth() - 1);
//     return d.getFullYear();
//   });
//   const [month, setMonth] = useState(() => {
//     const d = new Date();
//     d.setMonth(d.getMonth() - 1);
//     return String(d.getMonth() + 1);
//   });

//   const monthsList = [
//     { value: "1", name: "January" },
//     { value: "2", name: "February" },
//     { value: "3", name: "March" },
//     { value: "4", name: "April" },
//     { value: "5", name: "May" },
//     { value: "6", name: "June" },
//     { value: "7", name: "July" },
//     { value: "8", name: "August" },
//     { value: "9", name: "September" },
//     { value: "10", name: "October" },
//     { value: "11", name: "November" },
//     { value: "12", name: "December" },
//   ];

//   // Unique key to hold separate manual entries for different periods/managers
//   const currentAllocationKey = `${activeSalesmanId}-${year}-${month}`;
//   const currentAdjustment = adjustmentsLog[currentAllocationKey] ?? "";
//   const currentBonus = bonusLog[currentAllocationKey] ?? "";

//   // Dynamic Excel Export Engine (Formatted for Accounting and Print Layouts)
//   const handleExportToExcel = () => {
//     if (!commissionReport || commissionReport.length === 0) {
//       alert("No active reporting dataset available to export.");
//       return;
//     }
//     const report = commissionReport[0];
//     const monthName =
//       monthsList.find((m) => String(m.value) === String(month))?.name || month;

//     // ---- Style palette (accounting workbook look) ----
//     const COLOR = {
//       headerFill: "1E293B", // slate-800
//       headerText: "FFFFFF",
//       titleText: "1E293B",
//       zebraFill: "F8FAFC", // slate-50
//       borderGray: "CBD5E1", // slate-300
//       summaryLabel: "334155", // slate-700
//       grandFill: "EEF2FF", // indigo-50
//       grandText: "3730A3", // indigo-800
//       negativeRed: "DC2626",
//       positiveGreen: "047857",
//     };

//     const thinBorder = {
//       top: { style: "thin", color: { rgb: COLOR.borderGray } },
//       bottom: { style: "thin", color: { rgb: COLOR.borderGray } },
//       left: { style: "thin", color: { rgb: COLOR.borderGray } },
//       right: { style: "thin", color: { rgb: COLOR.borderGray } },
//     };

//     // Standard Excel accounting format
//     const accountingFmt =
//       '_-$* #,##0.00_-;[Red]-$* #,##0.00_-;_-$* "-"??_-;_-@_-';
//     const percentageFmt = '0.0"%";[Red]-0.0"%"';
//     const flatDollarFmt = "_-$* #,##0.00_-;[Red]-$* #,##0.00_-";

//     const COLS = 7; // A..G

//     const exportRows = [
//       [
//         viewMode === "channels" ? "CHANNEL ID" : "GROUP ID",
//         viewMode === "channels" ? "CHANNEL NAME" : "GROUP NAME",
//         "CURRENT SALES",
//         "PREVIOUS SALES",
//         "UP/DOWN (%)",
//         "COMM RATE",
//         "PAYOUT AMOUNT",
//       ],
//     ];

//     let totalCommissionCombined = 0;
//     let fallbackRate = 0;
//     // Running totals for the CURRENT SALES / PREVIOUS SALES / UP-DOWN columns,
//     // summed across every itemized row that gets pushed into the sheet.
//     let totalCurrentSalesSum = 0;
//     let totalPreviousSalesSum = 0;
//     const channelStartRow = exportRows.length;

//     // Export dataset switches context dynamically depending on current active view
//     const dataRecords =
//       viewMode === "channels" ? report.channels : report.groups;

//     if (dataRecords && dataRecords.length > 0) {
//       dataRecords.forEach((item) => {
//         totalCommissionCombined += item.commission?.amount || 0;
//         totalCurrentSalesSum += item.sales?.currentSales || 0;
//         totalPreviousSalesSum += item.sales?.previousSales || 0;
//         const numericPct = parseFloat(item.sales?.upDownPercent) || 0;
//         const numericRate = parseFloat(item.commission?.rate) || 0;
//         if (!item.commission?.isFlatAmount && numericRate > 0) {
//           fallbackRate = numericRate;
//         }
//         exportRows.push([
//           viewMode === "channels"
//             ? item.channelId
//             : item.groupId?.toUpperCase(),
//           viewMode === "channels"
//             ? item.channelName || `Channel ${item.channelId}`
//             : `${item.groupId?.toUpperCase()} MARKETPLACES`,
//           item.sales?.currentSales,
//           item.sales?.previousSales,
//           numericPct,
//           item.commission?.isFlatAmount ? item.commission?.rate : numericRate,
//           item.commission?.amount,
//         ]);
//       });
//     } else {
//       exportRows.push(["No configuration matrix entities found."]);
//     }

//     const parsedAdjustment = parseFloat(currentAdjustment) || 0;
//     const parsedBonus = parseFloat(currentBonus) || 0;

//     // Calculate commission driven by the adjustment line item
//     const commissionFromAdjustment = parsedAdjustment * fallbackRate;

//     // Append the custom Adjustment row into the main itemized listings array block
//     if (parsedAdjustment !== 0) {
//       totalCurrentSalesSum += parsedAdjustment;
//       exportRows.push([
//         "—",
//         "ADJUSTMENT",
//         parsedAdjustment,
//         0,
//         0,
//         fallbackRate,
//         commissionFromAdjustment,
//       ]);
//     }

//     const channelEndRow = exportRows.length - 1;
//     const totalAdjustedCommission =
//       totalCommissionCombined + commissionFromAdjustment;
//     const grandTotalCalculated = totalAdjustedCommission + parsedBonus;

//     // Aggregate UP/DOWN (%) for the totals row, based on the summed
//     // current/previous sales rather than averaging each row's percentage.
//     const totalUpDownPercent =
//       totalPreviousSalesSum !== 0
//         ? parseFloat(
//             (
//               ((totalCurrentSalesSum - totalPreviousSalesSum) /
//                 totalPreviousSalesSum) *
//               100
//             ).toFixed(2),
//           )
//         : 0;

//     exportRows.push(
//       [],
//       [
//         "",
//         "TOTAL",
//         totalCurrentSalesSum,
//         totalPreviousSalesSum,
//         totalUpDownPercent,
//         "",
//         totalAdjustedCommission,
//       ],
//       ["", "", "", "", "", "Bonus:", parsedBonus],
//       ["", "", "", "", "", "Grand Total:", grandTotalCalculated],
//     );

//     const summaryStartRow = channelEndRow + 2;
//     const grandTotalRow = summaryStartRow + 2;
//     const totalsRow = summaryStartRow; // "TOTAL" row (replaces old "Sub Total:" row)

//     const worksheet = XLSX.utils.aoa_to_sheet(exportRows);

//     // Header row styling
//     const headerRowIdx = 0;
//     for (let c = 0; c < COLS; c++) {
//       const cell = worksheet[XLSX.utils.encode_cell({ r: headerRowIdx, c })];
//       if (cell) {
//         cell.s = {
//           font: { bold: true, color: { rgb: COLOR.headerText }, sz: 10 },
//           fill: { fgColor: { rgb: COLOR.headerFill } },
//           alignment: {
//             horizontal: c === 1 ? "left" : c === 0 ? "left" : "right",
//             vertical: "center",
//           },
//           border: thinBorder,
//         };
//       }
//     }

//     // Data rows styling
//     if (dataRecords && exportRows.length > 1) {
//       for (let r = channelStartRow; r <= channelEndRow; r++) {
//         const isZebra = (r - channelStartRow) % 2 === 1;
//         const rowLabel = exportRows[r][1];
//         const isAdjustmentRow = rowLabel === "ADJUSTMENT";

//         for (let c = 0; c < COLS; c++) {
//           const cellRef = XLSX.utils.encode_cell({ r, c });
//           const cell = worksheet[cellRef];
//           if (!cell) continue;

//           const baseStyle = {
//             border: thinBorder,
//             fill: isZebra ? { fgColor: { rgb: COLOR.zebraFill } } : undefined,
//             font: { sz: 10, italic: isAdjustmentRow },
//             alignment: { vertical: "center" },
//           };

//           if (c === 0) {
//             cell.s = {
//               ...baseStyle,
//               font: { ...baseStyle.font, bold: true },
//               alignment: { ...baseStyle.alignment, horizontal: "center" },
//             };
//           } else if (c === 1) {
//             cell.s = {
//               ...baseStyle,
//               font: {
//                 ...baseStyle.font,
//                 bold: viewMode === "groups" || isAdjustmentRow,
//               },
//               alignment: { ...baseStyle.alignment, horizontal: "left" },
//             };
//           } else if (c === 2 || c === 3) {
//             cell.t = "n";
//             cell.z = accountingFmt;
//             cell.s = {
//               ...baseStyle,
//               alignment: { ...baseStyle.alignment, horizontal: "right" },
//             };
//           } else if (c === 4) {
//             cell.t = "n";
//             cell.z = percentageFmt;
//             cell.s = {
//               ...baseStyle,
//               alignment: { ...baseStyle.alignment, horizontal: "right" },
//             };
//           } else if (c === 5) {
//             const rowItem = dataRecords[r - channelStartRow];
//             const isRowFlat = rowItem?.commission?.isFlatAmount;

//             const rawValue = exportRows[r][c];
//             if (
//               typeof rawValue === "number" &&
//               !isRowFlat &&
//               rawValue < 0.01 &&
//               rawValue > 0
//             ) {
//               cell.t = "s";
//               cell.v = `${(rawValue * 100).toFixed(1)}%`;
//               cell.z = undefined;
//             } else {
//               cell.t = "n";
//               cell.z = isRowFlat ? flatDollarFmt : percentageFmt;
//             }
//             cell.s = {
//               ...baseStyle,
//               alignment: { ...baseStyle.alignment, horizontal: "right" },
//             };
//           } else if (c === 6) {
//             cell.t = "n";
//             cell.z = accountingFmt;
//             cell.s = {
//               ...baseStyle,
//               font: { ...baseStyle.font, bold: true },
//               alignment: { ...baseStyle.alignment, horizontal: "right" },
//             };
//           }
//         }
//       }
//     }

//     // ---- TOTAL row styling (CURRENT SALES / PREVIOUS SALES / UP-DOWN + commission) ----
//     {
//       const r = totalsRow;
//       const labelCell = worksheet[XLSX.utils.encode_cell({ r, c: 1 })];
//       const currentSalesCell = worksheet[XLSX.utils.encode_cell({ r, c: 2 })];
//       const previousSalesCell = worksheet[XLSX.utils.encode_cell({ r, c: 3 })];
//       const upDownCell = worksheet[XLSX.utils.encode_cell({ r, c: 4 })];
//       const commLabelCell = worksheet[XLSX.utils.encode_cell({ r, c: 5 })];
//       const commValueCell = worksheet[XLSX.utils.encode_cell({ r, c: 6 })];

//       const totalsBaseStyle = {
//         font: { bold: true, sz: 10, color: { rgb: COLOR.summaryLabel } },
//         fill: { fgColor: { rgb: COLOR.zebraFill } },
//         border: {
//           ...thinBorder,
//           top: { style: "medium", color: { rgb: COLOR.borderGray } },
//         },
//         alignment: { vertical: "center" },
//       };

//       if (labelCell) {
//         labelCell.s = {
//           ...totalsBaseStyle,
//           alignment: { ...totalsBaseStyle.alignment, horizontal: "left" },
//         };
//       }
//       if (currentSalesCell) {
//         currentSalesCell.t = "n";
//         currentSalesCell.z = accountingFmt;
//         currentSalesCell.s = {
//           ...totalsBaseStyle,
//           alignment: { ...totalsBaseStyle.alignment, horizontal: "right" },
//         };
//       }
//       if (previousSalesCell) {
//         previousSalesCell.t = "n";
//         previousSalesCell.z = accountingFmt;
//         previousSalesCell.s = {
//           ...totalsBaseStyle,
//           alignment: { ...totalsBaseStyle.alignment, horizontal: "right" },
//         };
//       }
//       if (upDownCell) {
//         upDownCell.t = "n";
//         upDownCell.z = percentageFmt;
//         upDownCell.s = {
//           ...totalsBaseStyle,
//           font: {
//             ...totalsBaseStyle.font,
//             color: {
//               rgb:
//                 totalUpDownPercent < 0 ? COLOR.negativeRed : COLOR.summaryLabel,
//             },
//           },
//           alignment: { ...totalsBaseStyle.alignment, horizontal: "right" },
//         };
//       }
//       if (commLabelCell) {
//         commLabelCell.s = {
//           ...totalsBaseStyle,
//           alignment: { ...totalsBaseStyle.alignment, horizontal: "right" },
//         };
//       }
//       if (commValueCell) {
//         commValueCell.t = "n";
//         commValueCell.z = accountingFmt;
//         commValueCell.s = {
//           ...totalsBaseStyle,
//           alignment: { ...totalsBaseStyle.alignment, horizontal: "right" },
//         };
//       }
//     }

//     const summaryLabels = ["Bonus:", "Grand Total:"];

//     summaryLabels.forEach((_, idx) => {
//       const r = summaryStartRow + 1 + idx;
//       const isGrand = r === grandTotalRow;

//       const labelCell = worksheet[XLSX.utils.encode_cell({ r, c: 5 })];
//       const valueCell = worksheet[XLSX.utils.encode_cell({ r, c: 6 })];

//       if (labelCell) {
//         labelCell.s = {
//           font: {
//             bold: true,
//             sz: isGrand ? 11 : 10,
//             color: { rgb: isGrand ? COLOR.grandText : COLOR.summaryLabel },
//           },
//           alignment: { horizontal: "right", vertical: "center" },
//           fill: isGrand ? { fgColor: { rgb: COLOR.grandFill } } : undefined,
//           border: isGrand
//             ? {
//                 ...thinBorder,
//                 top: { style: "double", color: { rgb: COLOR.grandText } },
//               }
//             : { bottom: { style: "hair", color: { rgb: COLOR.borderGray } } },
//         };
//       }
//       if (valueCell) {
//         valueCell.t = "n";
//         valueCell.z = accountingFmt;
//         valueCell.s = {
//           font: {
//             bold: true,
//             sz: isGrand ? 12 : 10,
//             color: { rgb: isGrand ? COLOR.grandText : COLOR.summaryLabel },
//           },
//           alignment: { horizontal: "right", vertical: "center" },
//           fill: isGrand ? { fgColor: { rgb: COLOR.grandFill } } : undefined,
//           border: isGrand
//             ? {
//                 ...thinBorder,
//                 top: { style: "double", color: { rgb: COLOR.grandText } },
//               }
//             : { bottom: { style: "hair", color: { rgb: COLOR.borderGray } } },
//         };
//       }
//     });

//     worksheet["!cols"] = [
//       { wch: 14 },
//       { wch: 26 },
//       { wch: 18 },
//       { wch: 18 },
//       { wch: 15 },
//       { wch: 14 },
//       { wch: 20 },
//     ];

//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, "Reconciliation Ledger");
//     const filename = `${report.name?.replace(/\s+/g, "_")}_${viewMode.toUpperCase()}_Commission_${monthName}_${year}.xlsx`;
//     XLSX.writeFile(workbook, filename);
//   };

//   // Unified accounting alignment engine ($ on left, number tightly wrapped in () on right)
//   const renderAccountingCell = (value, customColorClass = "text-slate-800") => {
//     const isNegative = value < 0;
//     const absoluteValue = Math.abs(value);
//     const formattedNumber = new Intl.NumberFormat("en-US", {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     }).format(absoluteValue);

//     const activeColorClass = isNegative
//       ? "text-red-600 font-bold"
//       : customColorClass;

//     return (
//       <div
//         className={`flex justify-between w-full font-mono text-sm tracking-tight ${activeColorClass}`}
//       >
//         <span>$</span>
//         <span>
//           {isNegative ? "(" : ""}
//           {formattedNumber}
//           {isNegative ? ")" : ""}
//         </span>
//       </div>
//     );
//   };

//   useEffect(() => {
//     const loadSalesmenProfiles = async () => {
//       try {
//         // Updated to use centralized api instance
//         const res = await api.get("/ecomm/managers");
//         const payload = res.data;
//         if (payload.success && payload.data.length > 0) {
//           setSalesmen(payload.data);
//           setActiveSalesmanId(payload.data[0].salesmanId);
//         }
//       } catch (err) {
//         console.error(
//           "Failed to connect to team roster dataset config arrays",
//           err,
//         );
//       }
//     };
//     loadSalesmenProfiles();
//   }, []);

//   useEffect(() => {
//     if (!activeSalesmanId || !year) return;
//     const fetchCommissionReportData = async () => {
//       setIsLoading(true);
//       try {
//         // Updated to use centralized api instance
//         const res = await api.get("/ecomm/commissions", {
//           params: { year, month, salesmanId: activeSalesmanId },
//         });
//         const payload = res.data;
//         if (payload.success) {
//           const reportData = Array.isArray(payload.data)
//             ? payload.data
//             : [payload.data];
//           setCommissionReport(reportData);
//         }
//       } catch (err) {
//         console.error(
//           "Error drawing live commission calculation pipelines",
//           err,
//         );
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchCommissionReportData();
//   }, [year, month, activeSalesmanId]);

//   return (
//     <div className="min-h-screen bg-slate-50 p-6 font-sans">
//       <div className="max-w-[1400px] mx-auto px-4 md:px-6">
//         {/* Page Header */}
//         <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b pb-5 mb-6 gap-4">
//           <div>
//             <h1 className="text-2xl font-bold text-slate-900">
//               E-Commerce Commission
//             </h1>
//           </div>
//           <Link
//             to="settings"
//             className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-800 font-medium text-sm py-2 px-4 rounded-lg shadow-sm transition-all"
//           >
//             <svg
//               className="w-4 h-4 text-slate-400"
//               fill="none"
//               viewBox="0 0 24 24"
//               stroke="currentColor"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
//               />
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
//               />
//             </svg>
//             Manager Settings
//           </Link>
//         </header>

//         {/* Control Bar */}
//         <div className="border-b border-slate-200 flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
//           <div className="flex flex-wrap gap-1 -mb-[2px]">
//             {salesmen.map((manager) => {
//               const isSelected = manager.salesmanId === activeSalesmanId;

//               const formatTabName = (fullName) => {
//                 if (!fullName) return "";
//                 const cleanName = fullName.replace("-", " ").trim();
//                 const parts = cleanName.split(/\s+/);
//                 const firstName = parts[0] || "";
//                 const numberMatch = fullName.match(/\b\d{4}\b/);
//                 const pbsIdNum = numberMatch ? numberMatch[0] : "";
//                 return pbsIdNum ? `${firstName} - ${pbsIdNum}` : firstName;
//               };

//               const formattedName = formatTabName(manager.name);

//               return (
//                 <button
//                   key={manager.salesmanId}
//                   onClick={() => setActiveSalesmanId(manager.salesmanId)}
//                   className={`px-4 py-2.5 text-sm font-semibold rounded-t-xl tracking-wide transition-all border-b-2 ${
//                     isSelected
//                       ? "border-indigo-600 bg-white text-indigo-600 font-bold shadow-sm"
//                       : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
//                   }`}
//                 >
//                   {formattedName}
//                   <span className="ml-2 text-xs font-mono bg-slate-200/80 text-slate-600 px-1.5 py-0.5 rounded font-bold">
//                     {manager.assignedChannelIds?.length || 0} Ch
//                   </span>
//                 </button>
//               );
//             })}
//           </div>

//           <div className="flex flex-wrap items-center gap-3 pb-2 md:pb-1">
//             <div className="bg-slate-200/70 p-0.5 rounded-lg inline-flex items-center shadow-inner border">
//               <button
//                 onClick={() => setViewMode("channels")}
//                 className={`text-xs px-2.5 py-1 rounded-md font-semibold transition-all duration-150 ${viewMode === "channels" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
//               >
//                 Channels View
//               </button>
//               <button
//                 onClick={() => setViewMode("groups")}
//                 className={`text-xs px-2.5 py-1 rounded-md font-semibold transition-all duration-150 ${viewMode === "groups" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
//               >
//                 Group View
//               </button>
//             </div>

//             <div className="w-24">
//               <input
//                 type="number"
//                 placeholder="Year"
//                 className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-indigo-600 font-mono text-center bg-white shadow-sm"
//                 value={year}
//                 onChange={(e) =>
//                   setYear(parseInt(e.target.value) || new Date().getFullYear())
//                 }
//               />
//             </div>
//             <div className="w-32">
//               <select
//                 className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-indigo-600 bg-white shadow-sm text-center font-medium text-slate-700"
//                 value={month}
//                 onChange={(e) => setMonth(e.target.value)}
//               >
//                 {monthsList.map((m) => (
//                   <option key={m.value} value={m.value}>
//                     {m.name}
//                   </option>
//                 ))}
//               </select>
//             </div>
//             <button
//               onClick={handleExportToExcel}
//               className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs py-1.5 px-3.5 rounded-lg shadow-sm transition-all tracking-wider uppercase"
//             >
//               <svg
//                 className="w-3.5 h-3.5"
//                 fill="none"
//                 viewBox="0 0 24 24"
//                 stroke="currentColor"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2.5}
//                   d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
//                 />
//               </svg>
//               Export Report
//             </button>
//           </div>
//         </div>

//         {/* Content Workspace */}
//         {isLoading ? (
//           <div className="bg-white border rounded-xl p-12 text-center text-sm text-slate-400 animate-pulse">
//             Loading database routing parameters...
//           </div>
//         ) : (
//           <div className="space-y-6">
//             {commissionReport.map((report) => {
//               if (!report) return null;

//               const activeRecords =
//                 viewMode === "channels" ? report.channels : report.groups;

//               const totalCommissionCombined =
//                 activeRecords?.reduce(
//                   (acc, c) => acc + c.commission.amount,
//                   0,
//                 ) || 0;

//               let standardAppliedRate = 0;
//               if (activeRecords && activeRecords.length > 0) {
//                 const foundItem = activeRecords.find(
//                   (item) =>
//                     !item.commission?.isFlatAmount &&
//                     parseFloat(item.commission?.rate) > 0,
//                 );
//                 if (foundItem) {
//                   standardAppliedRate =
//                     parseFloat(foundItem.commission.rate) / 100;
//                 }
//               }

//               const totalCurrentSales =
//                 activeRecords?.reduce(
//                   (acc, c) => acc + (c.sales?.currentSales || 0),
//                   0,
//                 ) || 0;

//               const totalPreviousSales =
//                 activeRecords?.reduce(
//                   (acc, c) => acc + (c.sales?.previousSales || 0),
//                   0,
//                 ) || 0;

//               const parsedAdjustment = parseFloat(currentAdjustment) || 0;
//               const parsedBonus = parseFloat(currentBonus) || 0;

//               const adjustedCurrentSalesTotal =
//                 totalCurrentSales + parsedAdjustment;
//               const commissionFromAdjustment =
//                 parsedAdjustment * standardAppliedRate;
//               const finalSubtotalCommission =
//                 totalCommissionCombined + commissionFromAdjustment;

//               const subtotalUpDownPercent =
//                 totalPreviousSales !== 0
//                   ? parseFloat(
//                       (
//                         ((adjustedCurrentSalesTotal - totalPreviousSales) /
//                           totalPreviousSales) *
//                         100
//                       ).toFixed(2),
//                     )
//                   : 0;

//               const grandTotalCalculated =
//                 finalSubtotalCommission + parsedBonus;

//               return (
//                 <div
//                   key={report.salesmanId}
//                   className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6"
//                 >
//                   <div className="overflow-x-auto">
//                     <table className="w-full text-left border-collapse text-sm">
//                       <thead>
//                         <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-mono text-xs uppercase">
//                           <th className="p-3 font-bold min-w-[320px] whitespace-nowrap lg:whitespace-normal">
//                             {viewMode === "channels"
//                               ? "Channel ID / Name"
//                               : "Group ID / Platform Name"}
//                           </th>
//                           <th className="p-3 text-right font-bold w-1/5">
//                             Current Sales
//                           </th>
//                           <th className="p-3 text-right font-bold w-1/5">
//                             Previous Sales
//                           </th>
//                           <th className="p-3 text-right font-bold w-1/5">
//                             UP/DOWN
//                           </th>
//                           <th className="p-3 text-right font-bold w-1/5">
//                             Comm Rate / Payout
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody className="divide-y divide-slate-100 font-mono">
//                         {activeRecords?.map((item) => (
//                           <tr
//                             key={
//                               viewMode === "channels"
//                                 ? item.channelId
//                                 : item.groupId
//                             }
//                             className="hover:bg-slate-50/50"
//                           >
//                             <td className="p-3 font-sans font-medium text-slate-700 flex items-center min-w-[320px]">
//                               {viewMode === "channels" ? (
//                                 <>
//                                   <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-mono mr-2 font-bold inline-block w-14 text-center">
//                                     {item.channelId}
//                                   </span>
//                                   <span className="text-sm font-medium">
//                                     {item.channelName ||
//                                       `Channel ${item.channelId}`}
//                                   </span>
//                                 </>
//                               ) : (
//                                 <>
//                                   <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-1 rounded text-xs font-mono mr-4 font-extrabold uppercase tracking-wide inline-block w-28 text-center shrink-0">
//                                     {item.groupId}
//                                   </span>
//                                   <span className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
//                                     {item.groupId} Marketplaces Total
//                                   </span>
//                                 </>
//                               )}
//                             </td>
//                             <td className="p-3 max-w-[180px]">
//                               {renderAccountingCell(
//                                 item.sales.currentSales,
//                                 "font-semibold text-slate-800",
//                               )}
//                             </td>
//                             <td className="p-3 max-w-[180px]">
//                               {renderAccountingCell(
//                                 item.sales.previousSales,
//                                 "text-slate-500",
//                               )}
//                             </td>
//                             <td className="p-3 max-w-[220px]">
//                               <div className="flex items-center justify-end gap-2">
//                                 <span
//                                   className={`text-xs font-bold shrink-0 ${item.sales.difference >= 0 ? "text-emerald-600" : "text-rose-600"}`}
//                                 >
//                                   {item.sales.difference < 0
//                                     ? `(${Math.abs(item.sales.upDownPercent)}%)`
//                                     : `${item.sales.upDownPercent}%`}
//                                 </span>
//                               </div>
//                             </td>
//                             <td className="p-3 max-w-[200px]">
//                               <div className="flex items-center justify-end gap-3">
//                                 <span className="text-xs text-slate-400 font-sans tracking-normal shrink-0">
//                                   {item.commission.isFlatAmount ? "$" : ""}
//                                   {item.commission.rate}
//                                   {item.commission.isFlatAmount ? "" : "%"}
//                                 </span>
//                                 <div className="w-full">
//                                   {renderAccountingCell(
//                                     item.commission.amount,
//                                     "font-bold text-indigo-600",
//                                   )}
//                                 </div>
//                               </div>
//                             </td>
//                           </tr>
//                         ))}

//                         {/* --- INLINE ADJUSTMENT ROW --- */}
//                         {activeRecords && activeRecords.length > 0 && (
//                           <tr className="bg-slate-50/40 border-t border-slate-200">
//                             <td className="p-3 font-sans text-xs uppercase font-bold tracking-wider text-slate-900 text-left">
//                               Adjustment
//                             </td>
//                             <td className="p-2 max-w-[180px]">
//                               <div className="relative flex items-center w-full bg-white border border-slate-200 rounded-md shadow-sm px-2">
//                                 <span className="text-slate-400 text-sm font-semibold mr-1">
//                                   $
//                                 </span>
//                                 <input
//                                   type="number"
//                                   step="0.01"
//                                   placeholder="0.00"
//                                   className="w-full text-right font-mono text-sm tracking-tight text-slate-800 focus:outline-none bg-transparent py-1"
//                                   value={currentAdjustment}
//                                   onChange={(e) =>
//                                     setAdjustmentsLog({
//                                       ...adjustmentsLog,
//                                       [currentAllocationKey]: e.target.value,
//                                     })
//                                   }
//                                 />
//                               </div>
//                             </td>
//                             <td className="p-3 text-right text-slate-400">—</td>
//                             <td className="p-3 text-right text-slate-400">—</td>
//                             <td className="p-3 max-w-[200px]">
//                               <div className="flex items-center justify-end gap-3">
//                                 <span className="text-xs text-slate-400 font-sans tracking-normal shrink-0 font-normal">
//                                   {(standardAppliedRate * 100).toFixed(1)}%
//                                 </span>
//                                 <div className="w-full">
//                                   {renderAccountingCell(
//                                     commissionFromAdjustment,
//                                     "font-bold text-indigo-500 italic",
//                                   )}
//                                 </div>
//                               </div>
//                             </td>
//                           </tr>
//                         )}

//                         {/* --- SUBTOTAL ROW --- */}
//                         {activeRecords && activeRecords.length > 0 && (
//                           <tr className="bg-slate-50/80 font-bold border-t-2 border-slate-300 border-b-2">
//                             <td className="p-3 font-sans text-xs uppercase tracking-wider text-slate-700 font-extrabold">
//                               Subtotal
//                             </td>
//                             <td className="p-3 max-w-[180px]">
//                               {renderAccountingCell(
//                                 adjustedCurrentSalesTotal,
//                                 "font-bold text-slate-800",
//                               )}
//                             </td>
//                             <td className="p-3 max-w-[180px]">
//                               {renderAccountingCell(
//                                 totalPreviousSales,
//                                 "font-bold text-slate-600",
//                               )}
//                             </td>
//                             <td className="p-3 max-w-[220px]">
//                               <div className="flex items-center justify-end gap-2">
//                                 <span
//                                   className={`text-xs font-extrabold shrink-0 ${subtotalUpDownPercent >= 0 ? "text-emerald-600" : "text-rose-600"}`}
//                                 >
//                                   {subtotalUpDownPercent < 0
//                                     ? `(${Math.abs(subtotalUpDownPercent)}%)`
//                                     : `${subtotalUpDownPercent}%`}
//                                 </span>
//                               </div>
//                             </td>
//                             <td className="p-3 max-w-[200px]">
//                               <div className="flex items-center justify-end gap-3">
//                                 <span className="text-xs text-slate-400 font-sans tracking-normal shrink-0 font-normal">
//                                   —
//                                 </span>
//                                 <div className="w-full">
//                                   {renderAccountingCell(
//                                     finalSubtotalCommission,
//                                     "font-extrabold text-indigo-600",
//                                   )}
//                                 </div>
//                               </div>
//                             </td>
//                           </tr>
//                         )}

//                         {(!activeRecords || activeRecords.length === 0) && (
//                           <tr>
//                             <td
//                               colSpan="5"
//                               className="p-6 text-center text-slate-400 italic font-sans"
//                             >
//                               No active parameters matched this configuration
//                               matrix display profile view.
//                             </td>
//                           </tr>
//                         )}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Manual Reconciliation Summary Ledger breakdown */}
//                   <div className="flex justify-end pt-4">
//                     <div className="w-full max-w-md border border-slate-200 rounded-lg overflow-hidden shadow-sm bg-slate-50/40">
//                       <table className="w-full text-left border-collapse text-sm font-mono">
//                         <tbody>
//                           <tr className="border-b border-slate-200">
//                             <td className="p-3 font-sans text-xs font-bold text-slate-400 uppercase tracking-wider">
//                               Sub Total Payout
//                             </td>
//                             <td className="p-3 w-44 bg-white/50">
//                               {renderAccountingCell(
//                                 finalSubtotalCommission,
//                                 "font-bold text-slate-700",
//                               )}
//                             </td>
//                           </tr>
//                           <tr className="border-b border-slate-200">
//                             <td className="p-3 font-sans text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">
//                               Bonus (Flat)
//                             </td>
//                             <td className="p-2 w-44 bg-white">
//                               <div className="relative flex items-center">
//                                 <span className="absolute left-2.5 text-slate-400 text-sm">
//                                   $
//                                 </span>
//                                 <input
//                                   type="number"
//                                   step="0.01"
//                                   placeholder="0.00"
//                                   className="w-full text-right border border-slate-200 rounded-md pl-6 pr-2 py-1 font-mono text-sm tracking-tight text-slate-800 focus:outline-indigo-600 focus:border-indigo-600"
//                                   value={currentBonus}
//                                   onChange={(e) =>
//                                     setBonusLog({
//                                       ...bonusLog,
//                                       [currentAllocationKey]: e.target.value,
//                                     })
//                                   }
//                                 />
//                               </div>
//                             </td>
//                           </tr>
//                           <tr className="bg-slate-100/60 font-bold">
//                             <td className="p-3 font-sans text-xs font-extrabold text-slate-900 uppercase tracking-wider">
//                               Grand Total
//                             </td>
//                             <td className="p-3 w-44 bg-indigo-50/30 border-b-4 border-double border-indigo-600">
//                               {renderAccountingCell(
//                                 grandTotalCalculated,
//                                 "text-base font-extrabold text-indigo-600",
//                               )}
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default EcomCommissions;
